import { BASE_URL } from '@ai-platform/utils';
import { useCallback, useMemo } from 'react';

import { ScreenSize, useGetScreenSize } from '../../hooks/useGetScreenSize';
import { buildCategoryPath } from '../../utils/url';
import { feedActions, useActions, useFeedSelector } from '../../store';

import { UseCategoriesFromUrlResult } from './HomePage.types';

/**
 * Хук для определения количества колонок в ленте промптов
 * в зависимости от размера экрана
 *
 * Соответствует логике CSS Grid из Feed.scss:
 * - mobile (до 374px): 1 колонка
 * - mobile (375px - 1023px): 2 колонки
 * - tablet (1024px - 1365px): 3 колонки
 * - desktop (1366px - 1919px): 4 колонки
 * - large-desktop (1440px - 1919px): 5 колонок
 * - extea-large-desktop (1920px+): 6 колонок
 *
 * @returns {PromptFeedColumns} Количество колонок для текущего размера экрана
 */
export const usePromptFeedColumns = (): number => {
    const screenSize = useGetScreenSize();

    switch (screenSize) {
        case ScreenSize.Mobile:
            return 2;
        case ScreenSize.Tablet:
            return 3;
        case ScreenSize.SmallDesktop:
            return 3;
        case ScreenSize.MiddleDesktop:
            return 4;
        case ScreenSize.Desktop:
            return 4;
        case ScreenSize.LargeDesktop:
            return 5;
        case ScreenSize.ExtraLargeDesktop:
            return 6;
        default:
            return 2; // fallback для неопределенных случаев
    }
};

export const useSelectedCategories = (): UseCategoriesFromUrlResult => {
    const { category } = useFeedSelector();
    const { setCategory } = useActions(feedActions);

    // Функция для обновления категории с синхронизацией URL и стора
    const updateSelectedCategories = useCallback(
        (categories: string[]) => {
            const newCategory = categories[0] || null;
            const newPath = buildCategoryPath(newCategory);

            // Обновляем стор
            setCategory(newCategory);

            // Обновляем URL с сохранением существующих search параметров
            if (typeof window !== 'undefined') {
                const currentUrl = new URL(window.location.href);
                const newUrl = new URL(BASE_URL + newPath, window.location.origin);

                // Копируем все существующие search параметры в новый URL
                currentUrl.searchParams.forEach((value, key) => {
                    newUrl.searchParams.set(key, value);
                });

                window.history.replaceState(null, '', newUrl.toString());
            }
        },
        [setCategory],
    );

    // Возвращаем массив для совместимости с существующим API компонентов
    const selectedCategories = useMemo(() => (category ? [category] : []), [category]);

    // Мемоизируем результат для избежания создания нового объекта при каждом рендере
    return useMemo(
        () => ({
            selectedCategories,
            updateSelectedCategories,
        }),
        [selectedCategories, updateSelectedCategories],
    );
};
